import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Textarea } from '@xo-union/tk-component-fields';
import { compose } from '@xo-union/react-css-modules';
import styles from './AiCustomPromptInput.styles.scss';
import { IconButton } from '../../../IconButton';

interface AiCustomPromptInputProps {
  isOpen: boolean;
  onSubmit: (prompt: string) => void;
  isSingleLine?: boolean;
  children?: React.ReactNode;
}

const AiCustomPromptInput = ({ isOpen, onSubmit, isSingleLine = false, children }: AiCustomPromptInputProps) => {
  const [customPromptValue, setCustomPromptValue] = React.useState('');

  const inputRef = React.useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    if (isOpen) {
      inputRef.current.focus();
    } else if (!isOpen) {
      if (!!customPromptValue.trim()) {
        inputRef.current.blur();
        setCustomPromptValue('');
      }

      inputRef.current.style.height = styles.textareaLineHeight;
    }
  }, [isOpen, customPromptValue]);

  const handleSubmit = (customPrompt: string, onSubmit: (prompt: string) => void) => {
    const trimmedPrompt = customPrompt.trim();
    if (trimmedPrompt === '') {
      return;
    }

    onSubmit(trimmedPrompt);
    setCustomPromptValue('');
  };

  const onEnterPressInTextarea = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();

        handleSubmit(customPromptValue, onSubmit);
      }
    },
    [customPromptValue, onSubmit]
  );

  useEffect(() => {
    const updateTextareaHeight = () => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = inputRef.current.scrollHeight + 2 + 'px';
      }
    };

    const currentInputRef = inputRef.current;

    currentInputRef?.addEventListener('input', updateTextareaHeight);
    currentInputRef?.addEventListener('keydown', onEnterPressInTextarea);

    return () => {
      currentInputRef?.removeEventListener('input', updateTextareaHeight);
      currentInputRef?.removeEventListener('keydown', onEnterPressInTextarea);
    };
  }, [onEnterPressInTextarea]);

  return (
    <div
      className={classNames(styles.AiCustomPromptInput__form, {
        [styles.AiCustomPromptInput__form__isOpen]: isOpen,
        [styles.AiCustomPromptInput__form__isClosed]: !isOpen,
      })}
      data-testid="AiCustomPromptInput__form"
    >
      <div
        className={classNames(styles.AiCustomPromptInput, {
          [styles.AiCustomPromptInput__isOpen]: isOpen,
          [styles.AiCustomPromptInput__isClosed]: !isOpen,
        })}
        data-testid="AiCustomPromptInput"
      >
        <div
          className={classNames(styles.AiCustomPromptInput__wrapper, {
            [styles.AiCustomPromptInput__wrapper__isOpen]: isOpen,
            [styles.AiCustomPromptInput__wrapper__isClosed]: !isOpen,
          })}
          data-testid="AiCustomPromptInput__wrapper"
        >
          <Textarea
            name="custom-prompt-input"
            label={false}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              setCustomPromptValue(event.currentTarget.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => onEnterPressInTextarea(e.nativeEvent)}
            value={customPromptValue}
            ref={inputRef}
            placeholder="Prompt anything"
            classes={compose({
              container: styles.AiCustomPromptInput__inputField__container,
              input: classNames(styles.AiCustomPromptInput__inputField__input, {
                [styles.AiCustomPromptInput__inputField__input__singleLine]: isSingleLine,
              }),
            })}
            rows={1}
            data-testid="AiCustomPromptInput__inputField"
            aria-label="Custom Prompt Input"
            aria-expanded={isOpen}
            tabIndex={isOpen ? 0 : -1}
          />
          {customPromptValue ? (
            <IconButton
              arialabel="Custom Prompt Submit"
              datatestid="AiCustomPromptInput__submitButton"
              name="forward"
              size="sm"
              className={styles.AiCustomPromptInput__submitButton}
              onClick={() => handleSubmit(customPromptValue, onSubmit)}
              onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => onEnterPressInTextarea(e.nativeEvent)}
            />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};

export default AiCustomPromptInput;
